import { getTimeTravelBlock } from '@/composables/useSnapshots';
import { PoolApr } from '@/providers/local/pool-rewards.provider';
import { balancerSubgraphService } from '@/services/balancer/subgraph/balancer-subgraph.service';
import { Pool } from '@/services/pool/types';
import { TokenInfoMap } from '@/types/TokenList';
import PoolService from '../pool.service';
import { PoolMulticaller } from './pool.multicaller';

/**
 * @summary Decorates a set of pools with additonal data.
 */
export class PoolDecorator {
  constructor(
    public pools: Pool[],
    public isPoolAprDataFetched?: ComputedRef<boolean>,
    public getPoolApr?: (address: `0x${string}`) => PoolApr | undefined,
    private readonly poolServiceClass = PoolService,
    private readonly poolSubgraph = balancerSubgraphService
  ) {}

  private async setPoolAprWithDelay(
    poolService: PoolService,
    maxRetries = 50,
    retryInterval = 200 // 50 retries * 200ms = 10 seconds max
  ) {
    // Wait for initial APR data to be available
    while (!this.isPoolAprDataFetched?.value && maxRetries > 0) {
      await new Promise(resolve => setTimeout(resolve, retryInterval));
      maxRetries--;
    }

    // do one more round of waiting to make sure we have the data
    await new Promise(resolve => setTimeout(resolve, retryInterval));

    // Once we have the APR data (or we've timed out), calculate the APR
    return await poolService.setAPR();
  }

  public async processPools(): Promise<Pool[]> {
    return Promise.all(
      this.pools.map(async pool => {
        const poolService = new this.poolServiceClass(pool);
        return poolService.pool;
      })
    );
  }

  public async decorate(
    tokens: TokenInfoMap,
    fullDecoration = true
  ): Promise<Pool[]> {
    const processedPools = this.pools.map(pool => {
      const poolService = new this.poolServiceClass(pool);
      return poolService.pool;
    });

    const poolMulticaller = new PoolMulticaller(processedPools);

    const [poolSnapshots, rawOnchainDataMap] = await Promise.all([
      fullDecoration ? this.getSnapshots() : [],
      poolMulticaller.fetch(),
    ]);

    const promises = processedPools.map(async pool => {
      const poolApr = computed(() =>
        this.getPoolApr?.(pool.address as `0x${string}`)
      );

      const poolService = new this.poolServiceClass(pool, poolApr);

      poolService.setOnchainData(rawOnchainDataMap[pool.id], tokens);

      // All of the following are pre-cached by the BurrBear API so we can skip
      // decoration of them if the pool came from the API.
      if (fullDecoration) {
        const poolSnapshot = poolSnapshots.find(p => p.id === pool.id);
        poolService.setFeesSnapshot(poolSnapshot);
        poolService.setVolumeSnapshot(poolSnapshot);
        await Promise.all([
          poolService.setTotalLiquidity(),
          this.setPoolAprWithDelay(poolService),
        ]);
      }

      return poolService.pool;
    });

    return await Promise.all(promises);
  }

  /**
   * Re-sets totalLiquidty on all pools, typically after prices have been updated.
   */
  public async reCalculateTotalLiquidities(): Promise<Pool[]> {
    return Promise.all(
      this.pools.map(async pool => {
        const poolService = new this.poolServiceClass(pool);
        await poolService.setTotalLiquidity();
        return poolService.pool;
      })
    );
  }

  /**
   * @summary Get snapshot data of pools
   * @description Getting the past state of pools allows us to calculate
   * snapshot values like volume and fees, currently fixed at past 24h
   * (see getTimeTravelBlock).
   */
  private async getSnapshots(): Promise<Pool[]> {
    const blockNumber = await getTimeTravelBlock();
    const block = { number: blockNumber };
    const isInPoolIds = { id: { in: this.pools.map(pool => pool.id) } };
    try {
      return await this.poolSubgraph.pools.get({
        where: isInPoolIds,
        block,
      });
    } catch (error) {
      console.error('Failed to fetch pool snapshots', error);
      return [];
    }
  }
}
